import { useEffect, useState, useRef } from 'react';
import Radio from 'components/ui-components/Radio';
import RadioContainer from 'components/ui-components/RadioContainer';
import Button from 'components/ui-components/Button';
import NewAddress from './delivery_address_new_address';
import AddressBook from './delivery_address_address_book';
import PaymentBillingAddressGuest from './payment_billing_address_guest';

const PaymentCard = ({
  signed_in: signedIn,
  addresses,
  setPaymentStatus,
  configGenericError,
  completeOrder,
  addressMode,
  delivery_address: deliveryAddress,
  customerInvoiceAddress,
  setInvoiceAddress,
  invoice_address: invoiceAddress,
  branchAddressFlag,
  toggleGenericError,
  addressEnquiryUrl,
  show_payment_display_error: showPaymentDisplayError,
  payment_display_errors: paymentDisplayErrors,
  paymentTotal,
  assets: {
    cards: { visa, electron, mastercard, maestro },
  },
  setInvoiceAddressFromAddressBook,
  billingAddressUrl,
  getAddressBook,
  current_step: currentStep,
  setAddressMode,
  telephone,
  showDetailErrors,
  itsPaymentUrl,
}) => {
  const [itsIframeUrl, setItsIframeUrl] = useState('');
  const [displayItsFrame, setDisplayItsFrame] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    line1: '',
    line2: '',
    line3: '',
    line4: '',
    line5: '',
    postcode: '',
  });
  const [billingAddressOption, setBillingAddressOption] = useState(
    signedIn ? 'invoice_address' : 'delivery_address',
  );
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(false);
  const [billingDifferentToDelivery, setBillingDifferentToDelivery] =
    useState(false);
  const [billingSameAsInvoice, setBillingSameAsInvoice] = useState(false);
  const [addressIsSelected, setAddressIsSelected] = useState(false);
  const [billingReference, setBillingReference] = useState('');
  const [billingAmount, setBillingAmount] = useState('');
  const [billingPostcode, setBillingPostcode] = useState('');

  const iframeRef = useRef(null);

  const reattemptPayment = () => {
    toggleGenericError();
    setDisplayItsFrame(false);
  };

  const reloadCheckout = () => {
    window.location.reload();
  };

  const paymentListener = event => {
    const domain = `${window.location.protocol}//${window.location.host}`;
    if (event.origin !== domain) {
      return;
    }
    if (event.data === 'its-payment-error') {
      setDisplayItsFrame(false);
      setBillingDifferentToDelivery(false);
      setPaymentStatus(false);
      configGenericError({
        title: 'Payment Error',
        body: (
          <div>
            <p>Unfortunately, your payment was not successful.</p>
            <p>No payment has been taken from your card.</p>
            <p>
              We are sorry for any inconvenience this may cause. Please try
              again later. If the problem persists, please contact our customer
              service team.
            </p>
          </div>
        ),
        errorButton: true,
        errorButtonTitle: 'Re-Attempt Payment',
        errorButtonAction: reattemptPayment,
        closeAction: reattemptPayment,
      });
    } else if (event.data === 'its-payment-restart') {
      setDisplayItsFrame(false);
      setBillingDifferentToDelivery(false);
      setPaymentStatus(false);
      configGenericError({
        title: 'Payment Error',
        body: (
          <div>
            <p>
              Sorry, this website cannot be open in more than one window or tab
              during payment
            </p>
            <p>
              Please close all other cef.co.uk windows or tabs and refresh the
              checkout to continue.
            </p>
          </div>
        ),
        errorButton: true,
        errorButtonTitle: 'Refresh Checkout',
        reloadCheckoutAfterClose: true,
        reloadCheckoutAfterErrorButtonAction: true,
        errorButtonAction: reloadCheckout,
        closeAction: reloadCheckout,
      });
    } else if (
      !Object.hasOwn(event.data, 'source') &&
      event.data.indexOf('its-payment-success') === 0
    ) {
      const url = event.data.replace('its-payment-success-', '');
      setDisplayItsFrame(false);
      completeOrder({
        url,
      });
    }
  };

  const setBillingAddressAsInvoiceAddress = () => {
    setBillingAddress({
      name: customerInvoiceAddress.name,
      line1: customerInvoiceAddress.line_1,
      line2: customerInvoiceAddress.line_2,
      line3: customerInvoiceAddress.line_3,
      line4: customerInvoiceAddress.line_4,
      line5: customerInvoiceAddress.line_5,
      postcode: customerInvoiceAddress.postcode,
    });
    setBillingSameAsInvoice(true);
  };

  const setBillingAddressAsDeliveryAddress = () => {
    if (addressMode !== 'BRANCH_DELIVERY') {
      setBillingAddress({
        name: deliveryAddress.name,
        line1: deliveryAddress.line1,
        line2: deliveryAddress.line2,
        line3: deliveryAddress.line3,
        line4: deliveryAddress.line4,
        line5: deliveryAddress.line5,
        postcode: deliveryAddress.postcode,
      });
      setBillingSameAsDelivery(true);
    } else {
      setBillingSameAsDelivery(false);
    }
  };

  useEffect(() => {
    if (window.addEventListener) {
      window.addEventListener('message', paymentListener, false);
    } else {
      window.attachEvent('onmessage', paymentListener);
    }

    if (signedIn) {
      setBillingAddressAsInvoiceAddress();
      setBillingAddressOption('invoice_address');
    } else {
      setBillingAddressAsDeliveryAddress();
    }
  }, []);

  const changeBillingAddressOption = option => {
    switch (option) {
      case 'delivery_address':
        setBillingAddressOption(option);
        setBillingAddressAsDeliveryAddress();
        setInvoiceAddress(deliveryAddress);
        break;
      case 'invoice_address':
        setBillingAddressOption(option);
        setBillingAddressAsInvoiceAddress();
        setInvoiceAddress(customerInvoiceAddress);
        break;
      case 'different_address':
        setBillingAddressOption(option);
        break;
      case 'new_address':
        setBillingAddressOption(option);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (signedIn) {
      // check for signed in, delivery not the same as invoice.
      if (deliveryAddress !== invoiceAddress) {
        // check for signed in, set billing addresss same as delivery.
        if (billingAddressOption === 'delivery_address') {
          setBillingAddress(deliveryAddress);
        } else if (billingAddressOption === 'invoice_address') {
          setBillingAddressAsInvoiceAddress();
        } else {
          // check for signed in, set as new billing address based on either address book or new address form.
          setBillingAddress(invoiceAddress);
        }
      }
    }
  }, [
    deliveryAddress,
    invoiceAddress,
    billingAddressOption,
    branchAddressFlag,
  ]);

  useEffect(() => {
    if (!signedIn) {
      // check for guest, same billing as delivery after billing is set.
      setBillingAddress(deliveryAddress);
    }
  }, [deliveryAddress]);

  useEffect(() => {
    if (!signedIn) {
      // check for guest, different billing as delivery on checkout payment panel address select.
      setBillingAddress(invoiceAddress);
    }
  }, [invoiceAddress]);

  useEffect(() => {
    if (!signedIn) {
      if (branchAddressFlag) {
        changeBillingAddressOption('different_address');
      }
    }
  }, [branchAddressFlag]);

  const getItsIframeUrl = () => {
    $.ajax({
      url: itsPaymentUrl,
      dataType: 'json',
      success: ({ url, reference, amount, postcode }) => {
        // TODO: Refactor this to useReducer
        showDetailErrors(false, []);
        setItsIframeUrl(url);
        setDisplayItsFrame(true);
        setBillingReference(reference);
        setBillingAmount(amount);
        setBillingPostcode(postcode);

        setPaymentStatus(true);
        if (iframeRef.current) {
          iframeRef.current.onload = () => {};
        }
      },
      error: e => {
        showDetailErrors(true, e.responseText);

        setItsIframeUrl('');
        setDisplayItsFrame(false);
        setBillingReference('');
        setBillingAmount('');
        setBillingPostcode('');
      },
    });
  };

  const progressToCardDetails = () => {
    $('html, body').animate(
      {
        scrollTop: $('.payment-card-details').offset().top,
      },
      1000,
    );
    if (Object.keys(billingAddress ?? {}).length) {
      getItsIframeUrl();
    }
  };

  const handleGuestBillingAddress = address => {
    setInvoiceAddress(address);

    if (currentStep === 'PAYMENT') {
      setAddressIsSelected(true);
      setBillingDifferentToDelivery(true);
      setBillingSameAsDelivery(false);
      setShowNewAddressForm(false);
    }
  };

  const isBillingSelectionInvalid =
    // Invalid if no billing option is selected
    (!billingSameAsDelivery &&
      !billingDifferentToDelivery &&
      !billingSameAsInvoice) ||
    // Invalid if billing is the same as delivery but is a branch order
    (billingSameAsDelivery && branchAddressFlag) ||
    // Invalid if user has chosen the "different address" option but the billing address should be the same as shipping
    (billingAddressOption === 'different_address' &&
      !billingDifferentToDelivery) ||
    // Invalid if user has chosen the "new address" option but the billing address should be the same as shipping
    (billingAddressOption === 'new_address' && !billingDifferentToDelivery) ||
    // Invalid if "different address" is selected but no address has been chosen
    (billingAddressOption === 'different_address' && !addressIsSelected);

  const isBillingConfirmationButtonDisabled =
    showPaymentDisplayError || displayItsFrame || isBillingSelectionInvalid;

  return (
    <div className="checkout-sub-panel payment">
      <div className="accepted-cards">
        <img src={visa} alt="Visa" />
        <img src={electron} alt="Electron" />
        <img src={mastercard} alt="Mastercard" />
        <img src={maestro} alt="Maestro" />
      </div>
      <div className="paypal-payment-summary">
        Your Payment Card will be charged:{' '}
        <strong>&pound;{paymentTotal}</strong> for your delivery items and
        delivery charge (if applicable).
      </div>

      <h3>Billing Address</h3>

      <p className="mb-16">
        Please ensure the billing address matches the address on your payment
        card
      </p>

      <div className="checkout-address">
        {(addressMode !== 'BRANCH_DELIVERY' || signedIn) && (
          <div className="checkout-address__column">
            <div
              className={`checkout-address__radios-container ${
                displayItsFrame ? ' disabled' : ''
              }`}>
              <RadioContainer groupName="billing">
                {signedIn && (
                  <Radio
                    id="invoice_address"
                    label="Same as Invoice Address"
                    value="invoice_address"
                    checked={
                      billingAddressOption === 'invoice_address' ||
                      ((addressMode === 'INVOICE_ADDRESS' ||
                        addressMode === 'BRANCH_DELIVERY') &&
                        billingAddressOption === 'delivery_address')
                    }
                    onChange={() => {
                      changeBillingAddressOption('invoice_address');
                    }}
                    disabled={displayItsFrame}
                  />
                )}
                {!branchAddressFlag && addressMode !== 'INVOICE_ADDRESS' && (
                  <Radio
                    id="delivery_address"
                    label="Same as Delivery Address"
                    value="delivery_address"
                    checked={billingAddressOption === 'delivery_address'}
                    onChange={() => {
                      changeBillingAddressOption('delivery_address');
                    }}
                    disabled={displayItsFrame}
                  />
                )}
                {signedIn && (
                  <Radio
                    id="different_address"
                    label="Select from Address Book"
                    value="different_address"
                    checked={billingAddressOption === 'different_address'}
                    onChange={() => {
                      changeBillingAddressOption('different_address');
                    }}
                    disabled={displayItsFrame}
                  />
                )}
                <Radio
                  id="new_address"
                  label="Enter a New Address"
                  value="new_address"
                  checked={billingAddressOption === 'new_address'}
                  onChange={() => {
                    changeBillingAddressOption('new_address');
                  }}
                  disabled={displayItsFrame}
                />
              </RadioContainer>
            </div>
          </div>
        )}
        {signedIn ? (
          <div className="checkout-address__column">
            <div className="checkout-address__address-container">
              <div className="checkout-address__address">
                <p>
                  {billingAddress?.name && (
                    <>
                      <strong>{billingAddress.name}</strong>
                      <br />
                    </>
                  )}
                  {billingAddress?.line1 && (
                    <>
                      {billingAddress.line1}
                      <br />
                    </>
                  )}
                  {billingAddress?.line2 && (
                    <>
                      {billingAddress.line2}
                      <br />
                    </>
                  )}
                  {billingAddress?.line3 && (
                    <>
                      {billingAddress.line3}
                      <br />
                    </>
                  )}
                  {billingAddress?.line4 && (
                    <>
                      {billingAddress.line4}
                      <br />
                    </>
                  )}
                  {billingAddress?.line5 && (
                    <>
                      {billingAddress.line5}
                      <br />
                    </>
                  )}
                  {billingAddress?.postcode && billingAddress.postcode}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <PaymentBillingAddressGuest
            billingAddress={billingAddress}
            addressMode={addressMode}
          />
        )}
      </div>
      {billingAddressOption === 'different_address' ? (
        signedIn ? (
          <div
            className={`postcode-lookup${displayItsFrame ? ' disabled' : ''}`}>
            {!showNewAddressForm && (
              <div>
                <AddressBook
                  type="payment"
                  addresses={addresses}
                  selectedAddress={billingAddress}
                  setAddress={setInvoiceAddressFromAddressBook}
                  handleGuestBillingAddress={handleGuestBillingAddress}
                  setAddressIsSelected={setAddressIsSelected}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className={`postcode-lookup${displayItsFrame ? ' disabled' : ''}`}>
            <NewAddress
              addressMode="NEW_ADDRESS"
              addressEnquiryUrl={addressEnquiryUrl}
              deliveryAddressUrl={billingAddressUrl}
              getAddressBook={getAddressBook}
              method="PUT"
              currentStep={currentStep}
              setAddressFromAddressBook={setInvoiceAddressFromAddressBook}
              setAddressMode={setAddressMode}
              telephone={telephone}
              signedIn={signedIn}
              progressToCardDetails={progressToCardDetails}
              handleGuestBillingAddress={handleGuestBillingAddress}
            />
          </div>
        )
      ) : (
        ''
      )}
      {billingAddressOption === 'new_address' && (
        <NewAddress
          addressMode="NEW_ADDRESS"
          addressEnquiryUrl={addressEnquiryUrl}
          deliveryAddressUrl={billingAddressUrl}
          getAddressBook={getAddressBook}
          method="PUT"
          currentStep={currentStep}
          setAddressFromAddressBook={setInvoiceAddressFromAddressBook}
          setAddressMode={setAddressMode}
          telephone={telephone}
          signedIn={signedIn}
          progressToCardDetails={progressToCardDetails}
          handleGuestBillingAddress={handleGuestBillingAddress}
        />
      )}
      {showPaymentDisplayError && (
        <div className="showPaymentDisplayError">
          {paymentDisplayErrors.map(errorMessage => (
            <p key={errorMessage}>{errorMessage}</p>
          ))}
        </div>
      )}

      <div className="fl-on fl-sm-on">
        <Button
          type="button"
          testId="card-payment-button"
          className="cef-btn cef-btn--green mt-20 m-mt-28"
          handleClick={progressToCardDetails}
          disabled={isBillingConfirmationButtonDisabled}>
          Continue to Card Details
        </Button>
      </div>

      <div className="payment-card-details">
        {Object.keys(billingAddress ?? {}).length && displayItsFrame && (
          <div>
            <h3>Please enter your card details</h3>
            <iframe
              title="its-iframe"
              className="its-frame"
              src={itsIframeUrl}
              ref={iframeRef}
              data-reference={billingReference}
              data-amount={billingAmount}
              data-postcode={billingPostcode}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCard;
